/**
 * Peer Scatter (Bubble) Chart — Value vs Quality.
 *
 * X = ROE (quality), Y = Fwd P/E (valuation), bubble size = market cap (log-scaled).
 * Target company rendered as a diamond in accent color; peers as circles.
 * Dashed median lines create four quadrants.
 */

import type Highcharts from "highcharts";
import { isDarkMode, setupThemeObserver } from "../../common/chartTheme";
import { formatMarketCap } from "../../dashboard/shared/formatters";
import { createChartLifecycle, type ChartLifecycleReflowOptions } from "./chartLifecycle";
import { escapeHtml } from "./chartUtils";
import type { PeerChartData } from "./peerChartTypes";
import { showChartEmptyState } from "./peerChartTypes";

/** A bubble data point with custom fields for tooltips and click. */
interface ScatterPoint {
  x: number;
  y: number;
  z: number;
  name: string;
  ticker: string;
  slug: string | null;
  marketcapFormatted: string;
  marker?: Highcharts.PointMarkerOptionsObject;
}

interface ScatterThemeColors {
  textColor: string;
  subtitleColor: string;
  gridLineColor: string;
  accentColor: string;
  peerColor: string;
  plotLineColor: string;
  tooltipBg: string;
  tooltipBorder: string;
  quadrantValue: string;
  quadrantGrowth: string;
  quadrantOvervalued: string;
  quadrantCheap: string;
}

let themeObserver: MutationObserver | null = null;

function getThemeColors(): ScatterThemeColors {
  const dark = isDarkMode();
  return {
    textColor: dark ? "#E0E0E3" : "#333333",
    subtitleColor: dark ? "#A0A0A3" : "#666666",
    gridLineColor: dark ? "#374151" : "#e5e7eb",
    accentColor: dark ? "#60a5fa" : "#0F52BA",
    peerColor: dark ? "#6b7280" : "#9ca3af",
    plotLineColor: dark ? "#606063" : "#999999",
    tooltipBg: dark ? "rgba(30, 30, 30, 0.95)" : "rgba(255, 255, 255, 0.98)",
    tooltipBorder: dark ? "#404043" : "#cccccc",
    quadrantValue: dark ? "#34d399" : "#059669",
    quadrantGrowth: dark ? "#818cf8" : "#2563eb",
    quadrantOvervalued: dark ? "#fb7185" : "#e11d48",
    quadrantCheap: dark ? "#fbbf24" : "#d97706",
  };
}

/**
 * Computes the median of a numeric array.
 */
function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function renderChartForContainer(container: HTMLElement): void {
  const configAttr = container.dataset.chartConfig;
  if (!configAttr) {
    lifecycle.destroyChart(container.id);
    return;
  }

  try {
    const scriptEl = document.getElementById(configAttr);
    if (!scriptEl) {
      lifecycle.destroyChart(container.id);
      return;
    }

    const data = JSON.parse(scriptEl.textContent || "{}") as PeerChartData;
    if (!data?.companies || data.companies.length === 0) {
      lifecycle.destroyChart(container.id);
      return;
    }

    if (!lifecycle.isContainerVisible(container)) {
      lifecycle.destroyChart(container.id);
      return;
    }

    lifecycle.destroyChart(container.id);
    const chart = renderScatterChart(container, data);
    if (chart) {
      lifecycle.instances.set(container.id, chart);
    }
  } catch (e) {
    console.warn(`Failed to render peer scatter chart for ${container.id}:`, e);
    showChartEmptyState(container, getThemeColors().textColor, "Chart could not be loaded");
  }
}

const lifecycle = createChartLifecycle(".peer-scatter-chart", renderChartForContainer, {
  deferUntilIntersect: true,
  rootMargin: "200px 0px",
});

/**
 * Renders the Value vs Quality scatter (bubble) chart.
 *
 * @param container - Target DOM element.
 * @param data - Parsed peer chart data.
 * @returns Highcharts chart instance or null.
 */
function renderScatterChart(container: HTMLElement, data: PeerChartData): Highcharts.Chart | null {
  if (!container || !window.Highcharts) return null;

  const colors = getThemeColors();

  // Only include companies with both ROE and Fwd P/E
  const valid = data.companies.filter((c) => c.roe !== null && c.fwdPe !== null);

  if (valid.length === 0) {
    showChartEmptyState(container, colors.textColor, "Insufficient data for scatter chart");
    return null;
  }

  const targetPoints: ScatterPoint[] = [];
  const peerPoints: ScatterPoint[] = [];

  for (const c of valid) {
    const point: ScatterPoint = {
      x: c.roe as number,
      y: c.fwdPe as number,
      z: Math.log10((c.marketcapUsd || 1) + 1),
      name: c.name,
      ticker: c.ticker,
      slug: c.slug,
      marketcapFormatted: formatMarketCap(c.marketcapUsd),
    };

    if (c.isTarget) {
      point.marker = {
        symbol: "diamond",
        radius: 8,
        lineWidth: 2,
        lineColor: colors.accentColor,
      };
      targetPoints.push(point);
    } else {
      peerPoints.push(point);
    }
  }

  // Compute medians for quadrant lines
  const allRoe = valid.map((c) => c.roe as number);
  const allFwdPe = valid.map((c) => c.fwdPe as number);
  const medianRoe = median(allRoe);
  const medianFwdPe = median(allFwdPe);

  const series: Highcharts.SeriesOptionsType[] = [];

  if (targetPoints.length > 0) {
    series.push({
      type: "bubble",
      name: targetPoints[0].name,
      color: colors.accentColor,
      data: targetPoints,
      marker: {
        symbol: "diamond",
        fillColor: colors.accentColor,
        lineWidth: 2,
        lineColor: colors.accentColor,
      },
    });
  }

  const labels = data.scatterLabels;
  const qLabels = labels?.quadrants;

  if (peerPoints.length > 0) {
    const quadrants = {
      value: {
        points: [] as ScatterPoint[],
        color: colors.quadrantValue,
        name: qLabels?.value ?? "Value",
      },
      growth: {
        points: [] as ScatterPoint[],
        color: colors.quadrantGrowth,
        name: qLabels?.growth ?? "Growth",
      },
      overvalued: {
        points: [] as ScatterPoint[],
        color: colors.quadrantOvervalued,
        name: qLabels?.overvalued ?? "Overvalued",
      },
      cheap: {
        points: [] as ScatterPoint[],
        color: colors.quadrantCheap,
        name: qLabels?.cheap ?? "Cheap",
      },
    };

    for (const point of peerPoints) {
      const highRoe = point.x >= medianRoe;
      const highPe = point.y >= medianFwdPe;
      if (highRoe && !highPe) quadrants.value.points.push(point);
      else if (highRoe && highPe) quadrants.growth.points.push(point);
      else if (!highRoe && highPe) quadrants.overvalued.points.push(point);
      else quadrants.cheap.points.push(point);
    }

    for (const q of Object.values(quadrants)) {
      if (q.points.length > 0) {
        series.push({
          type: "bubble",
          name: q.name,
          color: q.color,
          data: q.points,
          opacity: 0.7,
          marker: {
            symbol: "circle",
            fillOpacity: 0.7,
            lineWidth: 1,
            lineColor: q.color,
          },
        });
      }
    }
  }

  const quadrantBands: Highcharts.XAxisPlotBandsOptions[] = [
    {
      from: -Infinity,
      to: medianRoe,
      color: "transparent",
      label: {
        text: qLabels?.overvalued ?? "Overvalued",
        align: "left",
        verticalAlign: "top",
        style: { color: colors.quadrantOvervalued, fontSize: "9px", opacity: 0.6 },
        y: 12,
        x: 4,
      },
    },
    {
      from: -Infinity,
      to: medianRoe,
      color: "transparent",
      label: {
        text: qLabels?.cheap ?? "Cheap",
        align: "left",
        verticalAlign: "bottom",
        style: { color: colors.quadrantCheap, fontSize: "9px", opacity: 0.6 },
        y: -4,
        x: 4,
      },
    },
    {
      from: medianRoe,
      to: Infinity,
      color: "transparent",
      label: {
        text: qLabels?.growth ?? "Growth",
        align: "right",
        verticalAlign: "top",
        style: { color: colors.quadrantGrowth, fontSize: "9px", opacity: 0.6 },
        y: 12,
        x: -4,
      },
    },
    {
      from: medianRoe,
      to: Infinity,
      color: "transparent",
      label: {
        text: qLabels?.value ?? "Value",
        align: "right",
        verticalAlign: "bottom",
        style: { color: colors.quadrantValue, fontSize: "9px", opacity: 0.6 },
        y: -4,
        x: -4,
      },
    },
  ];

  const chart = window.Highcharts.chart(container, {
    chart: {
      type: "bubble",
      backgroundColor: "transparent",
      spacing: [10, 15, 15, 10],
      zooming: { type: "xy" },
    },
    title: {
      text: labels?.title ?? "Value vs Quality",
      align: "left",
      style: { color: colors.textColor, fontSize: "14px", fontWeight: "600" },
    },
    credits: { enabled: false },
    exporting: { enabled: false },
    navigation: { buttonOptions: { enabled: false } },
    xAxis: {
      title: {
        text: labels?.xAxis ?? "ROE (%)",
        style: { color: colors.subtitleColor, fontSize: "11px" },
      },
      gridLineWidth: 1,
      gridLineColor: colors.gridLineColor,
      labels: { style: { color: colors.subtitleColor, fontSize: "10px" } },
      plotLines: [
        {
          value: medianRoe,
          color: colors.plotLineColor,
          dashStyle: "Dash",
          width: 1,
          label: {
            text: labels?.medianRoe ?? "Median ROE",
            style: { color: colors.subtitleColor, fontSize: "9px" },
            align: "right",
          },
        },
      ],
      plotBands: quadrantBands,
    },
    yAxis: {
      title: {
        text: labels?.yAxis ?? "Fwd P/E (x)",
        style: { color: colors.subtitleColor, fontSize: "11px" },
      },
      gridLineColor: colors.gridLineColor,
      labels: { style: { color: colors.subtitleColor, fontSize: "10px" } },
      plotLines: [
        {
          value: medianFwdPe,
          color: colors.plotLineColor,
          dashStyle: "Dash",
          width: 1,
          label: {
            text: labels?.medianFwdPe ?? "Median Fwd P/E",
            style: { color: colors.subtitleColor, fontSize: "9px" },
          },
        },
      ],
    },
    tooltip: {
      useHTML: true,
      headerFormat: "",
      formatter() {
        const p = this.point as unknown as ScatterPoint;
        const name = escapeHtml(p.name);
        const ticker = escapeHtml(p.ticker);
        const mcap = escapeHtml(p.marketcapFormatted);
        return (
          '<div style="padding:6px;">' +
          `<strong style="font-size:13px;">${name}</strong><br/>` +
          `<span style="color:#888;">${ticker}</span>` +
          '<hr style="margin:4px 0;border-color:#ddd;"/>' +
          '<table style="font-size:11px;">' +
          `<tr><td style="padding-right:8px;">ROE:</td><td><b>${p.x.toFixed(1)}%</b></td></tr>` +
          `<tr><td style="padding-right:8px;">Fwd P/E:</td><td><b>${p.y.toFixed(1)}x</b></td></tr>` +
          `<tr><td style="padding-right:8px;">Market Cap:</td><td><b>${mcap}</b></td></tr>` +
          "</table>" +
          '<hr style="margin:4px 0;border-color:#ddd;"/>' +
          '<span style="font-size:10px;color:#3b82f6;">Click to view company</span>' +
          "</div>"
        );
      },
      backgroundColor: colors.tooltipBg,
      borderColor: colors.tooltipBorder,
      style: { color: colors.textColor },
    },
    plotOptions: {
      bubble: {
        minSize: valid.length > 25 ? 6 : 8,
        maxSize: valid.length <= 10 ? 35 : valid.length <= 25 ? 25 : 18,
        cursor: "pointer",
        stickyTracking: false,
        dataLabels: {
          enabled: valid.length <= 40,
          allowOverlap: false,
          format: "{point.ticker}",
          style: {
            fontSize: valid.length > 15 ? "8px" : "9px",
            fontWeight: "normal",
            textOutline: "none",
            color: colors.subtitleColor,
          },
        },
        point: {
          events: {
            click: function () {
              const pointData = this as unknown as { slug?: string };
              if (pointData.slug) {
                window.location.assign(`/companies/${pointData.slug}/`);
              }
            },
          },
        },
        animation: { duration: 400 },
      },
    },
    legend: {
      enabled: true,
      itemStyle: { color: colors.textColor, fontSize: "11px", fontWeight: "normal" },
    },
    series,
  } as Highcharts.Options) as Highcharts.Chart;

  return chart;
}

/**
 * Initializes all peer scatter charts on the page.
 */
export function initializePeerScatterCharts(
  root: ParentNode = document,
  options: ChartLifecycleReflowOptions = {},
): void {
  const containers = lifecycle.getContainers(root);
  const activeIds = new Set<string>();

  containers.forEach((container) => {
    if (!container.id) {
      container.id = `peer-scatter-${crypto.randomUUID().slice(0, 8)}`;
    }
    activeIds.add(container.id);
    lifecycle.reflowChartForContainer(container, options);
  });

  lifecycle.pruneStaleCharts(activeIds, root);

  if (!themeObserver) {
    themeObserver = setupThemeObserver(() => {
      initializePeerScatterCharts(document, { forceRender: true });
    });
  }
}

/**
 * Registers DOM listeners to auto-initialize peer scatter charts.
 */
export function setupPeerScatterChartAutoInit(): void {
  lifecycle.setupAutoInit(initializePeerScatterCharts);
}
